/**
 * PublishToPortalSection.js
 *
 * Reusable "Publish to Portal" button + confirmation dialog + published status row.
 * Follows the Invoice publish-to-portal pattern exactly.
 *
 * Props:
 *   deliverableId  — sale_owner_deliverable id
 *   publishedAt    — ISO timestamp if already published
 *   publishedBy    — name/email of who published
 *   onRefresh      — called after any publish/unpublish action
 *   disabled       — show button but greyed (guard condition not met)
 *   disabledReason — tooltip text when disabled
 */
import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Globe, Undo2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { fmtDatetime } from '../../utils/dateUtils';
import { useAuth } from '../../context/AuthContext';

const PublishToPortalSection = ({
  deliverableId,
  ownerName,
  publishedAt,
  publishedBy,
  onRefresh,
  disabled = false,
  disabledReason = 'Complete this step before publishing to the portal',
  publishEndpoint = null,    // override default endpoint (for questionnaires etc.)
  unpublishEndpoint = null,
}) => {
  const [dialogOpen, setDialogOpen]     = useState(false);
  const [publishing, setPublishing]     = useState(false);
  const [unpublishing, setUnpublishing] = useState(false);

  const { userTimezone } = useAuth();

  const isPublished = Boolean(publishedAt);

  const handlePublish = async () => {
    setPublishing(true);
    try {
      const url = publishEndpoint || `/sale-owner-deliverables/${deliverableId}/publish-to-portal`;
      await api.post(url);
      toast.success('Published to owner portal');
      setDialogOpen(false);
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to publish');
    } finally {
      setPublishing(false);
    }
  };

  const handleUnpublish = async () => {
    setUnpublishing(true);
    try {
      const url = unpublishEndpoint || `/sale-owner-deliverables/${deliverableId}/unpublish-from-portal`;
      await api.post(url);
      toast.success('Removed from owner portal');
      onRefresh?.();
    } catch (e) {
      toast.error('Failed to unpublish');
    } finally {
      setUnpublishing(false);
    }
  };

  if (isPublished) {
    return (
      <div className="flex items-center gap-2 flex-wrap" data-testid="publish-status-row">
        <div className="flex items-center gap-2 text-sm border rounded-md px-3 py-1.5 bg-blue-50 border-blue-200 flex-1 min-w-0">
          <Globe className="w-4 h-4 text-blue-600 flex-shrink-0" />
          <span className="text-blue-700 font-medium text-xs truncate">
            Published to Portal
            {publishedAt ? ` on ${fmtDatetime(publishedAt, userTimezone)}` : ''}
            {publishedBy ? ` by ${publishedBy}` : ''}
          </span>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleUnpublish}
          disabled={unpublishing}
          data-testid="unpublish-portal-btn"
        >
          {unpublishing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Undo2 className="w-3.5 h-3.5 mr-1" />}
          Undo Publish
        </Button>
      </div>
    );
  }

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="gap-1.5"
        onClick={() => setDialogOpen(true)}
        disabled={disabled}
        title={disabled ? disabledReason : undefined}
        data-testid="publish-portal-btn"
      >
        <Globe className="w-4 h-4" />
        Publish to Portal
      </Button>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Publish to Owner Portal</DialogTitle>
            <DialogDescription className="pt-1">
              This document will be published to the{' '}
              {ownerName
                ? <span className="font-semibold">{ownerName}</span>
                : 'owner'}{' '}
              Owner Portal, where they can view and download it. No email will be sent.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handlePublish}
              disabled={publishing}
              className="bg-primary hover:bg-primary/90 text-white"
              data-testid="confirm-publish-btn"
            >
              {publishing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Publishing…</> : 'Publish to Portal'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PublishToPortalSection;
